// AdminPromptsPanel — EquaOS's base interview prompts, one per round for
// every school (@story HIR-HR21; owner, 30 Oct 2026: "EquaOS only, all
// schools"). Only an EquaOS platform administrator saves, approves, rejects
// or resets (the server checks the token; anyone else gets its plain 403,
// shown as is). A school changes an interview through its brief, on the
// role's AI interview stage. There is no per-school override any more.
//
// Routes used:
//   GET   /api/admin/prompts/:kind
//   PUT   /api/admin/prompts/:kind
//   POST  /api/admin/prompts/:kind/approve | reject | reset

import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { Button, Card, Icon, Input, Select, useShellLayout } from "../../ds";
import { Page, PageHeader, Section, Loading } from "../../components/page";

const KINDS = [
  { key: "pre_screening",          label: "Pre-screening — CV Verification (Mira)" },
  { key: "psychoanalytical",       label: "Psychoanalytical — Mindset & Culture (Dr. Patel)" },
  { key: "final_interview",        label: "Final Interview — Technical + HR (Senior Panel)" },
  { key: "gen_ai_readiness",       label: "Gen AI Readiness Interview (Nova)" },
  { key: "situational_judgement",  label: "Situational Judgement Test" },
];

export default function AdminPromptsPanel() {
  const layout = useShellLayout();
  const mobile = Boolean(layout && layout.mobile);
  const [kind, setKind]           = useState(KINDS[0].key);
  const [prompt, setPrompt]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [axesInput, setAxesInput] = useState("");

  /* A change waiting for approval is what is edited and shown; the live
     version keeps running until an approver approves (30 Oct 2026). */
  const shown = (d) => (d && d.pending ? { ...d, ...Object.fromEntries(Object.entries(d.pending).filter(([k, v]) => v !== undefined && !/^proposed/.test(k))) } : d);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/prompts/${kind}`);
      setPrompt(shown(r.data));
      setAxesInput((shown(r.data)?.gradingAxes || []).join(", "));
    } catch (e) {
      toast.error("Failed to load prompt");
      setPrompt(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const save = async () => {
    if (!prompt) return;
    setSaving(true);
    try {
      const payload = {
        label:           prompt.label,
        systemPrompt:    prompt.systemPrompt,
        opener:          prompt.opener,
        questionTarget:  prompt.questionTarget,
        gradingAxes:     axesInput.split(",").map(s => s.trim()).filter(Boolean),
        durationMinutes: prompt.durationMinutes,
      };
      const r = await api.put(`/admin/prompts/${kind}`, payload);
      setPrompt(shown(r.data));
      toast.success(r.data?.message || "Saved for approval");
    } catch (e) {
      toast.error(e.response?.data?.message || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset to the shipped default, for every school? Edits will be lost.")) return;
    setSaving(true);
    try {
      await api.post(`/admin/prompts/${kind}/reset`);
      await load();
      toast.success("Reset to default");
    } catch (e) {
      toast.error(e.response?.data?.message || "Reset failed");
    } finally { setSaving(false); }
  };

  const decide = async (approve) => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/prompts/${kind}/${approve ? "approve" : "reject"}`);
      toast.success(r.data?.message || (approve ? "Approved" : "Rejected"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not record the decision");
    } finally { setSaving(false); }
  };

  const update = (field, value) => setPrompt(p => ({ ...p, [field]: value }));

  const grid = (min) => ({ display: "grid", gap: 16, gridTemplateColumns: mobile ? "1fr" : `repeat(auto-fit,minmax(${min}px,1fr))` });

  return (
    <Page>
      <PageHeader
        title="EquaOS base prompts"
        subtitle="One base prompt per interview round, used by every school. Only EquaOS administrators change or approve them; a school changes an interview through its brief."
      />

      <Section title="Which round">
        <div style={grid(260)}>
          <Select
            label="Round"
            value={kind}
            onChange={setKind}
            options={KINDS.map((k) => ({ value: k.key, label: k.label }))}
          />
        </div>
      </Section>

      {/* Editor */}
      {loading && <Card><Loading /></Card>}

      {!loading && prompt && (
        <Section
          title={prompt.label || "Prompt"}
          subtitle={`Version ${prompt.version || 1} · every school`}
          action={(
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button variant="ghost" size="sm" icon={<Icon name="arrow-clockwise" />} onClick={reset} disabled={saving}>
                Reset to default
              </Button>
              <Button size="sm" onClick={save} loading={saving}>
                Save for approval
              </Button>
            </div>
          )}
        >
          <div style={{ display: "grid", gap: 18 }}>
            {prompt.pending ? (
              <div role="status" style={{ display: "grid", gap: 8, padding: 12, borderRadius: "var(--radius-md)", background: "var(--status-warning-bg)", color: "var(--status-warning-text)" }}>
                <span>
                  Waiting for approval — proposed by {prompt.pending.proposedByName || "someone"}
                  {prompt.pending.proposedAt ? ` on ${new Date(prompt.pending.proposedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}` : ""}.
                  {" "}Every school's interviews use version {prompt.version || 1} until it is approved. Another EquaOS administrator, not the proposer, approves.
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button size="sm" onClick={() => decide(true)} disabled={saving}>Approve</Button>
                  <Button size="sm" variant="ghost" onClick={() => decide(false)} disabled={saving}>Reject</Button>
                </div>
              </div>
            ) : prompt.approvedByName ? (
              <div style={{ fontSize: "var(--fs-caption)", color: "var(--text-muted)" }}>
                In use: version {prompt.version || 1}, approved by {prompt.approvedByName}{prompt.approvedAt ? ` on ${new Date(prompt.approvedAt).toLocaleDateString("en-IN", { dateStyle: "medium" })}` : ""}.
              </div>
            ) : null}
            {prompt.elevenAgentId && (
              <div style={{ fontSize: "var(--fs-caption)", color: "var(--text-muted)" }}>
                Agent: <code style={{ color: "var(--text-2)" }}>{prompt.elevenAgentId}</code>
              </div>
            )}

            <Input label="Label" value={prompt.label || ""} onChange={(e) => update("label", e.target.value)} />

            <Input
              label="Opener"
              hint="The first thing the agent says. Use {{candidateName}} and {{jobTitle}}."
              multiline rows={3}
              value={prompt.opener || ""}
              onChange={(e) => update("opener", e.target.value)}
            />

            <Input
              label="System Prompt"
              hint="Persona + guardrails + interview goal. The agent's brain."
              multiline rows={18}
              value={prompt.systemPrompt || ""}
              onChange={(e) => update("systemPrompt", e.target.value)}
            />

            <div style={grid(180)}>
              <Input
                label="Question target"
                type="number"
                value={prompt.questionTarget || 8}
                onChange={(e) => update("questionTarget", Number(e.target.value))}
              />
              <Input
                label="Duration (minutes)"
                type="number"
                value={prompt.durationMinutes || 20}
                onChange={(e) => update("durationMinutes", Number(e.target.value))}
              />
              <Input
                label="Grading axes"
                hint="Comma-separated"
                value={axesInput}
                onChange={(e) => setAxesInput(e.target.value)}
                placeholder="Technical, Communication, ..."
              />
            </div>
          </div>
        </Section>
      )}
    </Page>
  );
}
